/**
 * Errors are thrown as i18n keys (`eimzo.errors.*`) — translate with `t(e.message)`.
 *
 * E-IMZO mijoz kutubxonasi (rasmiy CAPIWS protokoli).
 * Kompyuterda o'rnatilgan E-IMZO dasturi (https://e-imzo.soliq.uz) lokal WebSocket
 * `wss://127.0.0.1:64443/service/cryptapi` orqali ishlaydi — bu e-imzo.js / e-imzo-client.js
 * ichida ishlatiladigan protokolning o'zi (so'nggi versiyalar bilan mos).
 */

const WS_URL = window.location.protocol === 'https:'
  ? 'wss://127.0.0.1:64443/service/cryptapi'
  : 'ws://127.0.0.1:64646/service/cryptapi';

/** E-IMZO talab qilgan eng past versiya */
export const EIMZO_MIN_VERSION = { major: 3, minor: 37 };

/** Domen uchun API kalitlari (localhost va 127.0.0.1 uchun rasmiy demo kalitlar). Prod domen kaliti keyin qo'shiladi. */
const API_KEYS: string[] = [
  'localhost', '96D0C1491615C82B9A54D9989779DF825B690748224C2B04F500F370D51827CE2644D8D4A82C18184D73AB8530BB8ED537269603F61DB0D03D2104ABF789970B',
  '127.0.0.1', 'A7BCFA5D490B351BE0754130DF03A068F855DB4333D43921125B9CF2670EF6A40370C646B90401955E1F7BC9CDBF59CE0B2C5467D820BE189C845D0B79CFC96F',
];

export interface EimzoCert {
  disk: string;
  path: string;
  name: string;
  alias: string;
  serial: string;
  cn: string;
  tin: string;
  pinfl: string;
  org: string;
  validTo: string;
  expired: boolean;
}

type EimzoResponse = Record<string, unknown>;

function call(payload: Record<string, unknown>, timeoutMs = 15000): Promise<EimzoResponse> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try { ws = new WebSocket(WS_URL); } catch (e) { reject(e); return; }
    const t = setTimeout(() => { ws.close(); reject(new Error('eimzo.errors.timeout')); }, timeoutMs);
    ws.onerror = () => { clearTimeout(t); reject(new Error('eimzo.errors.not_found')); };
    ws.onopen = () => ws.send(JSON.stringify(payload));
    ws.onmessage = (ev) => {
      clearTimeout(t);
      ws.close();
      try {
        const data = JSON.parse(ev.data) as EimzoResponse;
        if (data.success === false) reject(new Error(String(data.reason || 'eimzo.errors.generic')));
        else resolve(data);
      } catch { reject(new Error('eimzo.errors.bad_response')); }
    };
  });
}

function parseAlias(alias: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of alias.split(',')) {
    const i = part.indexOf('=');
    if (i > 0) out[part.slice(0, i).trim().toLowerCase()] = part.slice(i + 1).trim();
  }
  return out;
}

export async function eimzoInit(): Promise<string> {
  const v = await call({ name: 'version' });
  const major = Number(v.major), minor = Number(v.minor);
  if (major < EIMZO_MIN_VERSION.major || (major === EIMZO_MIN_VERSION.major && minor < EIMZO_MIN_VERSION.minor)) {
    throw new Error('eimzo.errors.old_version');
  }
  await call({ name: 'apikey', arguments: API_KEYS });
  return `${major}.${minor}`;
}

export async function eimzoListCertificates(): Promise<EimzoCert[]> {
  const res = await call({ plugin: 'pfx', name: 'list_all_certificates' });
  const now = Date.now();
  const list = (res.certificates ?? []) as Array<Record<string, string>>;
  return list.map((c) => {
    const a = parseAlias(String(c.alias ?? ''));
    const validTo = (a.validto ?? '').replace(/\./g, '-');
    return {
      disk: c.disk, path: c.path, name: c.name, alias: c.alias,
      serial: a.serialnumber ?? '', cn: a.cn ?? '', tin: a['1.2.860.3.16.1.1'] ?? '',
      pinfl: a['1.2.860.3.16.1.2'] ?? '', org: a.o ?? '', validTo,
      expired: validTo ? new Date(validTo).getTime() < now : false,
    } as EimzoCert;
  });
}

function toBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

/** Kalitni yuklab, ma'lumotni PKCS#7 bilan imzolaydi (parol oynasini E-IMZO o'zi chiqaradi). */
export async function eimzoSign(cert: EimzoCert, data: string): Promise<string> {
  const loaded = await call({ plugin: 'pfx', name: 'load_key', arguments: [cert.disk, cert.path, cert.name, cert.alias] }, 120000);
  const res = await call({ plugin: 'pkcs7', name: 'create_pkcs7', arguments: [toBase64(data), String(loaded.keyId), 'no'] }, 120000);
  return String(res.pkcs7_64);
}
